import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, getAuthHeaders, handleApiError } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Card } from '../ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import {
  ArrowLeft, Plus, Trash2, Users, TrendingDown, TrendingUp, Banknote,
  BookOpen, Wallet, IndianRupee,
} from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (n) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);

const todayISO = () => new Date().toISOString().split('T')[0];
const currentMonth = () => new Date().toISOString().slice(0, 7);

const PropertyDetail = ({ propertyId, onBack }) => {
  const [property, setProperty] = useState(null);
  const [partners, setPartners] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [rents, setRents] = useState([]);
  const [loans, setLoans] = useState([]);
  const [ledger, setLedger] = useState({ rows: [], totals: { inflow: 0, outflow: 0, net: 0 } });
  const [loading, setLoading] = useState(true);

  const refreshAll = useCallback(async () => {
    try {
      const [pRes, prRes, exRes, rtRes, lnRes, ldRes] = await Promise.all([
        axios.get(`${API}/properties/${propertyId}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/properties/${propertyId}/partners`, { headers: getAuthHeaders() }),
        axios.get(`${API}/property-expenses?property_id=${propertyId}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/property-rents?property_id=${propertyId}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/property-loans?property_id=${propertyId}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/properties/${propertyId}/ledger`, { headers: getAuthHeaders() }),
      ]);
      setProperty(pRes.data);
      setPartners(prRes.data || []);
      setExpenses(exRes.data || []);
      setRents(rtRes.data || []);
      setLoans(lnRes.data || []);
      setLedger(ldRes.data || { rows: [], totals: { inflow: 0, outflow: 0, net: 0 } });
    } catch (err) {
      toast.error(handleApiError(err, 'Failed to load property data'));
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  if (loading || !property) {
    return <div className="text-slate-500 py-12 text-center">Loading...</div>;
  }

  const totalInvestment = partners.reduce((s, p) => s + (Number(p.investment_amount) || 0), 0);
  const totalExpenses = expenses.reduce((s, e) => s + (Number(e.amount) || 0), 0);
  const totalRentReceived = rents.reduce((s, r) => s + (Number(r.received) || 0), 0);
  const totalRentPending = rents.reduce((s, r) => s + (Number(r.pending) || 0), 0);
  const totalLoanBalance = loans.reduce((s, l) => s + (Number(l.balance) || 0), 0);

  return (
    <div className="space-y-5" data-testid="property-detail-root">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-to-properties">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <div className="flex-1">
          <h2 className="text-2xl font-heading tracking-tight text-slate-900">{property.name}</h2>
          <p className="text-sm text-slate-500">
            {property.address || '—'} · <span className="capitalize">{property.type}</span>
            {property.value ? ` · Value ${formatCurrency(property.value)}` : ''}
          </p>
        </div>
      </div>

      {/* Mini Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <MiniCard label="Partner Investment" value={formatCurrency(totalInvestment)} icon={Users} tone="blue" />
        <MiniCard label="Expenses" value={formatCurrency(totalExpenses)} icon={TrendingDown} tone="rose" />
        <MiniCard label="Rent Received" value={formatCurrency(totalRentReceived)} icon={TrendingUp} tone="emerald" />
        <MiniCard label="Rent Pending" value={formatCurrency(totalRentPending)} icon={Wallet} tone="amber" />
        <MiniCard label="Loan Balance" value={formatCurrency(totalLoanBalance)} icon={Banknote} tone="purple" />
      </div>

      <Tabs defaultValue="partners" className="w-full">
        <TabsList className="grid grid-cols-5 max-w-3xl">
          <TabsTrigger value="partners" data-testid="tab-partners"><Users className="h-4 w-4 mr-1" />Partners</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expenses"><TrendingDown className="h-4 w-4 mr-1" />Expenses</TabsTrigger>
          <TabsTrigger value="rents" data-testid="tab-rents"><TrendingUp className="h-4 w-4 mr-1" />Rent</TabsTrigger>
          <TabsTrigger value="loans" data-testid="tab-loans"><Banknote className="h-4 w-4 mr-1" />Loans</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="tab-ledger"><BookOpen className="h-4 w-4 mr-1" />Ledger</TabsTrigger>
        </TabsList>

        <TabsContent value="partners" className="mt-5">
          <PartnersPanel propertyId={propertyId} partners={partners} onRefresh={refreshAll} />
        </TabsContent>
        <TabsContent value="expenses" className="mt-5">
          <ExpensesPanel propertyId={propertyId} partners={partners} expenses={expenses} onRefresh={refreshAll} />
        </TabsContent>
        <TabsContent value="rents" className="mt-5">
          <RentsPanel propertyId={propertyId} rents={rents} onRefresh={refreshAll} />
        </TabsContent>
        <TabsContent value="loans" className="mt-5">
          <LoansPanel propertyId={propertyId} loans={loans} partners={partners} onRefresh={refreshAll} />
        </TabsContent>
        <TabsContent value="ledger" className="mt-5">
          <LedgerPanel ledger={ledger} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

const MiniCard = ({ label, value, icon: Icon, tone }) => {
  const tones = {
    blue: 'text-blue-600 bg-blue-50',
    rose: 'text-rose-600 bg-rose-50',
    emerald: 'text-emerald-600 bg-emerald-50',
    amber: 'text-amber-600 bg-amber-50',
    purple: 'text-purple-600 bg-purple-50',
  };
  return (
    <Card className="p-3 border-slate-200">
      <div className="flex items-center gap-2 mb-1">
        <div className={`w-7 h-7 rounded-md flex items-center justify-center ${tones[tone]}`}>
          <Icon className="h-3.5 w-3.5" />
        </div>
        <span className="text-xs font-medium text-slate-600 truncate">{label}</span>
      </div>
      <p className="text-lg font-heading tracking-tight text-slate-900">{value}</p>
    </Card>
  );
};

/* ========================= PARTNERS ========================= */
const PartnersPanel = ({ propertyId, partners, onRefresh }) => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', contribution_percent: '', investment_amount: '', notes: '' });
  const [payOpen, setPayOpen] = useState(null); // partner id
  const [payments, setPayments] = useState([]);
  const [payForm, setPayForm] = useState({ amount: '', date: todayISO(), kind: 'investment', notes: '' });

  const reset = () => {
    setForm({ name: '', contribution_percent: '', investment_amount: '', notes: '' });
    setEditing(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Partner name required');
    const payload = {
      name: form.name.trim(),
      contribution_percent: parseFloat(form.contribution_percent) || 0,
      investment_amount: parseFloat(form.investment_amount) || 0,
      notes: form.notes || null,
    };
    try {
      if (editing) {
        await axios.put(`${API}/partners/${editing.id}`, payload, { headers: getAuthHeaders() });
        toast.success('Partner updated');
      } else {
        await axios.post(`${API}/properties/${propertyId}/partners`, payload, { headers: getAuthHeaders() });
        toast.success('Partner added');
      }
      setOpen(false);
      reset();
      onRefresh();
    } catch (err) {
      toast.error(handleApiError(err, 'Failed to save partner'));
    }
  };

  const removePartner = async (p) => {
    if (!window.confirm(`Remove partner "${p.name}"? All their payments will be deleted.`)) return;
    try {
      await axios.delete(`${API}/partners/${p.id}`, { headers: getAuthHeaders() });
      toast.success('Partner removed');
      onRefresh();
    } catch (err) {
      toast.error(handleApiError(err, 'Failed to remove partner'));
    }
  };

  const openPayments = async (partner) => {
    setPayOpen(partner);
    try {
      const res = await axios.get(`${API}/partners/${partner.id}/payments`, { headers: getAuthHeaders() });
      setPayments(res.data || []);
    } catch (err) {
      toast.error('Failed to load payments');
    }
  };

  const submitPayment = async (e) => {
    e.preventDefault();
    if (!payForm.amount) return toast.error('Amount is required');
    try {
      await axios.post(
        `${API}/partners/${payOpen.id}/payments`,
        { ...payForm, amount: parseFloat(payForm.amount) },
        { headers: getAuthHeaders() }
      );
      toast.success('Payment recorded');
      setPayForm({ amount: '', date: todayISO(), kind: 'investment', notes: '' });
      // re-fetch
      const res = await axios.get(`${API}/partners/${payOpen.id}/payments`, { headers: getAuthHeaders() });
      setPayments(res.data || []);
      onRefresh();
    } catch (err) {
      toast.error(handleApiError(err, 'Failed to record payment'));
    }
  };

  const totalContribution = partners.reduce((s, p) => s + (Number(p.contribution_percent) || 0), 0);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">
          {partners.length} partner(s) · {totalContribution}% allocated
        </p>
        <Button
          size="sm"
          onClick={() => { reset(); setOpen(true); }}
          className="bg-blue-600 hover:bg-blue-700 text-white"
          data-testid="add-partner-btn"
        >
          <Plus className="h-4 w-4 mr-1" /> Add Partner
        </Button>
      </div>

      {partners.length === 0 ? (
        <Card className="p-8 text-center border-dashed text-slate-500">No partners yet.</Card>
      ) : (
        <Card className="border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left">Name</th>
                <th className="px-4 py-2 text-right">Share %</th>
                <th className="px-4 py-2 text-right">Net Investment</th>
                <th className="px-4 py-2 text-left">Notes</th>
                <th className="px-4 py-2 w-32"></th>
              </tr>
            </thead>
            <tbody>
              {partners.map((p) => (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2 font-medium text-slate-800">{p.name}</td>
                  <td className="px-4 py-2 text-right">{p.contribution_percent || 0}%</td>
                  <td className="px-4 py-2 text-right font-medium">{formatCurrency(p.investment_amount)}</td>
                  <td className="px-4 py-2 text-slate-500 truncate max-w-[200px]">{p.notes || '—'}</td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-emerald-600"
                      onClick={() => openPayments(p)}
                      data-testid={`partner-payments-${p.id}`}
                    >
                      <IndianRupee className="h-3.5 w-3.5 mr-1" /> Payments
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8"
                      onClick={() => removePartner(p)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Add/Edit Partner */}
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? 'Edit Partner' : 'Add Partner'}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                data-testid="partner-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Share %</Label>
                <Input
                  type="number"
                  value={form.contribution_percent}
                  onChange={(e) => setForm({ ...form, contribution_percent: e.target.value })}
                  placeholder="0"
                  step="0.01"
                />
              </div>
              <div>
                <Label>Initial Investment (₹)</Label>
                <Input
                  type="number"
                  value={form.investment_amount}
                  onChange={(e) => setForm({ ...form, investment_amount: e.target.value })}
                  placeholder="0"
                />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="submit-partner-btn">
                {editing ? 'Save Changes' : 'Add Partner'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Partner Payments Dialog */}
      <Dialog open={!!payOpen} onOpenChange={(o) => !o && setPayOpen(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Payments — {payOpen?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitPayment} className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end">
            <div className="col-span-1">
              <Label>Amount (₹)</Label>
              <Input
                type="number"
                value={payForm.amount}
                onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
                required
                data-testid="partner-payment-amount"
              />
            </div>
            <div className="col-span-1">
              <Label>Date</Label>
              <Input
                type="date"
                value={payForm.date}
                onChange={(e) => setPayForm({ ...payForm, date: e.target.value })}
              />
            </div>
            <div className="col-span-1">
              <Label>Type</Label>
              <Select value={payForm.kind} onValueChange={(v) => setPayForm({ ...payForm, kind: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="investment">Investment (+)</SelectItem>
                  <SelectItem value="drawing">Drawing (−)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-1">
              <Label>Notes</Label>
              <Input value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} />
            </div>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white col-span-1" data-testid="submit-partner-payment">
              Record
            </Button>
          </form>
          <div className="mt-2 max-h-72 overflow-y-auto">
            {payments.length === 0 ? (
              <p className="text-center text-slate-500 py-6 text-sm">No payments recorded.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-left">Type</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                    <th className="px-3 py-2 text-left">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.id} className="border-t border-slate-100">
                      <td className="px-3 py-2">{p.date}</td>
                      <td className="px-3 py-2 capitalize">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${p.kind === 'investment' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                          {p.kind}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-medium">{formatCurrency(p.amount)}</td>
                      <td className="px-3 py-2 text-slate-500">{p.notes || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const PROPERTY_EXPENSE_CATEGORIES = [
  'Construction',
  'Electricity',
  'Maintenance',
  'Legal',
  'Loan EMI',
  'Repair',
  'Miscellaneous',
];

/* ========================= EXPENSES ========================= */
const ExpensesPanel = ({ propertyId, partners, expenses, onRefresh }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    category: '',
    amount: '',
    date: todayISO(),
    partner_id: '',
    bill_ref: '',
    notes: '',
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.category || !form.amount) {
      return toast.error('Category and amount are required');
    }
    if (form.category === 'Loan EMI') {
      return toast.error("'Loan EMI' is auto-generated. Use the Loans tab → Pay EMI.");
    }
    try {
      await axios.post(`${API}/property-expenses`, {
        property_id: propertyId,
        category: form.category,
        amount: parseFloat(form.amount),
        date: form.date,
        partner_id: form.partner_id || null,
        bill_ref: form.bill_ref || null,
        notes: form.notes || null,
      }, { headers: getAuthHeaders() });
      toast.success('Expense added');
      setOpen(false);
      setForm({ category: '', amount: '', date: todayISO(), partner_id: '', bill_ref: '', notes: '' });
      onRefresh();
    } catch (err) {
      toast.error(handleApiError(err, 'Failed to add expense'));
    }
  };

  const remove = async (e) => {
    if (e.source === 'auto_emi') {
      return toast.error('Auto-generated EMI — delete from the Loans tab instead.');
    }
    if (!window.confirm('Delete this expense?')) return;
    try {
      await axios.delete(`${API}/property-expenses/${e.id}`, { headers: getAuthHeaders() });
      toast.success('Expense deleted');
      onRefresh();
    } catch (err) {
      toast.error(handleApiError(err, 'Failed to delete expense'));
    }
  };

  const total = expenses.reduce((s, e) => s + (Number(e.amount) || 0), 0);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">
          {expenses.length} expense(s) · Total {formatCurrency(total)}
        </p>
        <Button
          size="sm"
          onClick={() => setOpen(true)}
          className="bg-rose-600 hover:bg-rose-700 text-white"
          data-testid="add-property-expense-btn"
        >
          <Plus className="h-4 w-4 mr-1" /> Add Expense
        </Button>
      </div>

      {expenses.length === 0 ? (
        <Card className="p-8 text-center border-dashed text-slate-500">No expenses yet.</Card>
      ) : (
        <Card className="border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left">Date</th>
                <th className="px-4 py-2 text-left">Category</th>
                <th className="px-4 py-2 text-left">Paid By</th>
                <th className="px-4 py-2 text-left">Bill Ref</th>
                <th className="px-4 py-2 text-left">Notes</th>
                <th className="px-4 py-2 text-right">Amount</th>
                <th className="px-4 py-2 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {expenses.map((e) => (
                <tr key={e.id} className="border-t border-slate-100">
                  <td className="px-4 py-2 whitespace-nowrap">{e.date}</td>
                  <td className="px-4 py-2 font-medium">
                    <div className="flex items-center gap-2">
                      <span>{e.category}</span>
                      {e.source === 'auto_emi' && (
                        <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-purple-100 text-purple-700 border border-purple-200" title="Auto-generated from Loan EMI">
                          AUTO
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2">{e.partner_name || '—'}</td>
                  <td className="px-4 py-2 text-slate-500">{e.bill_ref || '—'}</td>
                  <td className="px-4 py-2 text-slate-500 truncate max-w-[200px]">{e.notes || '—'}</td>
                  <td className="px-4 py-2 text-right font-medium text-rose-600">{formatCurrency(e.amount)}</td>
                  <td className="px-4 py-2 text-right">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 disabled:opacity-30"
                      onClick={() => remove(e)}
                      disabled={e.source === 'auto_emi'}
                      title={e.source === 'auto_emi' ? 'Auto EMI — manage from Loans tab' : 'Delete'}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add Property Expense</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category *</Label>
                <Select
                  value={form.category}
                  onValueChange={(v) => setForm({ ...form, category: v })}
                >
                  <SelectTrigger data-testid="expense-category-trigger">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROPERTY_EXPENSE_CATEGORIES.filter((c) => c !== 'Loan EMI').map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-slate-400 mt-1">'Loan EMI' is auto-generated from the Loans tab.</p>
              </div>
              <div>
                <Label>Amount (₹) *</Label>
                <Input
                  type="number"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  required
                  data-testid="expense-amount-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
              <div>
                <Label>Paid By Partner</Label>
                <Select
                  value={form.partner_id || 'none'}
                  onValueChange={(v) => setForm({ ...form, partner_id: v === 'none' ? '' : v })}
                >
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {partners.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Bill Reference</Label>
              <Input value={form.bill_ref} onChange={(e) => setForm({ ...form, bill_ref: e.target.value })} />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="submit-property-expense">
                Add Expense
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ========================= RENTS ========================= */
const RentsPanel = ({ propertyId, rents, onRefresh }) => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    month: currentMonth(),
    tenant_name: '',
    expected: '',
    received: '',
    received_date: '',
    notes: '',
  });

  const reset = () => {
    setForm({ month: currentMonth(), tenant_name: '', expected: '', received: '', received_date: '', notes: '' });
    setEditing(null);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      month: r.month,
      tenant_name: r.tenant_name || '',
      expected: r.expected || '',
      received: r.received || '',
      received_date: r.received_date || '',
      notes: r.notes || '',
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.month) return toast.error('Month is required');
    const payload = {
      property_id: propertyId,
      month: form.month,
      tenant_name: form.tenant_name || null,
      expected: parseFloat(form.expected) || 0,
      received: parseFloat(form.received) || 0,
      received_date: form.received_date || null,
      notes: form.notes || null,
    };
    try {
      if (editing) {
        await axios.put(`${API}/property-rents/${editing.id}`, payload, { headers: getAuthHeaders() });
        toast.success('Rent updated');
      } else {
        await axios.post(`${API}/property-rents`, payload, { headers: getAuthHeaders() });
        toast.success('Rent added');
      }
      setOpen(false);
      reset();
      onRefresh();
    } catch (err) {
      toast.error(handleApiError(err, 'Failed to save rent'));
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete rent record for ${r.month}?`)) return;
    try {
      await axios.delete(`${API}/property-rents/${r.id}`, { headers: getAuthHeaders() });
      toast.success('Rent deleted');
      onRefresh();
    } catch (err) {
      toast.error('Failed to delete rent');
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">{rents.length} rent record(s)</p>
        <Button
          size="sm"
          onClick={() => { reset(); setOpen(true); }}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="add-rent-btn"
        >
          <Plus className="h-4 w-4 mr-1" /> Add Rent
        </Button>
      </div>

      {rents.length === 0 ? (
        <Card className="p-8 text-center border-dashed text-slate-500">No rent entries yet.</Card>
      ) : (
        <Card className="border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left">Month</th>
                <th className="px-4 py-2 text-left">Tenant</th>
                <th className="px-4 py-2 text-right">Expected</th>
                <th className="px-4 py-2 text-right">Received</th>
                <th className="px-4 py-2 text-right">Pending</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {rents.map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="px-4 py-2 font-medium">{r.month}</td>
                  <td className="px-4 py-2">{r.tenant_name || '—'}</td>
                  <td className="px-4 py-2 text-right">{formatCurrency(r.expected)}</td>
                  <td className="px-4 py-2 text-right text-emerald-600 font-medium">{formatCurrency(r.received)}</td>
                  <td className="px-4 py-2 text-right text-amber-600">{formatCurrency(r.pending)}</td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs capitalize ${
                      r.status === 'received' ? 'bg-emerald-100 text-emerald-700' :
                      r.status === 'partial' ? 'bg-amber-100 text-amber-700' :
                      'bg-rose-100 text-rose-700'
                    }`}>{r.status}</span>
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(r)}>
                      <Wallet className="h-4 w-4 text-slate-500" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => remove(r)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? 'Update Rent' : 'Add Rent Record'}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Month *</Label>
                <Input
                  type="month"
                  value={form.month}
                  onChange={(e) => setForm({ ...form, month: e.target.value })}
                  required
                  data-testid="rent-month-input"
                />
              </div>
              <div>
                <Label>Tenant Name</Label>
                <Input value={form.tenant_name} onChange={(e) => setForm({ ...form, tenant_name: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Expected (₹)</Label>
                <Input type="number" value={form.expected} onChange={(e) => setForm({ ...form, expected: e.target.value })} data-testid="rent-expected-input" />
              </div>
              <div>
                <Label>Received (₹)</Label>
                <Input type="number" value={form.received} onChange={(e) => setForm({ ...form, received: e.target.value })} data-testid="rent-received-input" />
              </div>
            </div>
            <div>
              <Label>Received Date</Label>
              <Input type="date" value={form.received_date} onChange={(e) => setForm({ ...form, received_date: e.target.value })} />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="submit-rent-btn">
                {editing ? 'Save Changes' : 'Add Rent'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ========================= LOANS ========================= */
const LoansPanel = ({ propertyId, loans, partners, onRefresh }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ lender: '', principal: '', emi: '', start_date: todayISO(), partner_id: '', notes: '' });
  const [payOpen, setPayOpen] = useState(null);
  const [payments, setPayments] = useState([]);

  const previewTenure = () => {
    const p = parseFloat(form.principal) || 0;
    const e = parseFloat(form.emi) || 0;
    return e > 0 ? Math.ceil(p / e) : 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.lender.trim() || !form.principal || !form.emi) {
      return toast.error('Lender, Loan Amount and EMI are required');
    }
    try {
      await axios.post(`${API}/property-loans`, {
        property_id: propertyId,
        lender: form.lender.trim(),
        principal: parseFloat(form.principal),
        emi: parseFloat(form.emi),
        start_date: form.start_date || null,
        partner_id: form.partner_id || null,
        notes: form.notes || null,
      }, { headers: getAuthHeaders() });
      toast.success('Loan added');
      setOpen(false);
      setForm({ lender: '', principal: '', emi: '', start_date: todayISO(), partner_id: '', notes: '' });
      onRefresh();
    } catch (err) {
      toast.error(handleApiError(err, 'Failed to add loan'));
    }
  };

  const remove = async (l) => {
    if (!window.confirm(`Delete loan from "${l.lender}"? All EMI history & auto-generated expenses will be removed.`)) return;
    try {
      await axios.delete(`${API}/property-loans/${l.id}`, { headers: getAuthHeaders() });
      toast.success('Loan deleted');
      onRefresh();
    } catch (err) {
      toast.error('Failed to delete loan');
    }
  };

  const payEMI = async (l) => {
    if (!window.confirm(`Record this month's EMI of ${formatCurrency(l.emi)} for ${l.lender}?\n\nThis will:\n• Reduce balance by ${formatCurrency(Math.min(l.emi, l.balance))}\n• Auto-create a 'Loan EMI' expense\n• Advance next due date by 1 month`)) return;
    try {
      await axios.post(`${API}/property-loans/${l.id}/pay-emi`, {}, { headers: getAuthHeaders() });
      toast.success("EMI recorded — balance updated");
      onRefresh();
    } catch (err) {
      toast.error(handleApiError(err, 'Failed to pay EMI'));
    }
  };

  const openPayments = async (loan) => {
    setPayOpen(loan);
    try {
      const res = await axios.get(`${API}/property-loans/${loan.id}/payments`, { headers: getAuthHeaders() });
      setPayments(res.data || []);
    } catch (err) {
      toast.error('Failed to load payments');
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">{loans.length} loan(s) · EMIs auto-post on due date and on the 1st of every month via cron.</p>
        <Button
          size="sm"
          onClick={() => setOpen(true)}
          className="bg-purple-600 hover:bg-purple-700 text-white"
          data-testid="add-loan-btn"
        >
          <Plus className="h-4 w-4 mr-1" /> Add Loan
        </Button>
      </div>

      {loans.length === 0 ? (
        <Card className="p-8 text-center border-dashed text-slate-500">No loans yet.</Card>
      ) : (
        <Card className="border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Lender</th>
                <th className="px-3 py-2 text-right">Loan Amt</th>
                <th className="px-3 py-2 text-right">EMI</th>
                <th className="px-3 py-2 text-right">Tenure</th>
                <th className="px-3 py-2 text-right">Paid</th>
                <th className="px-3 py-2 text-right">Balance</th>
                <th className="px-3 py-2 text-left">Next Due</th>
                <th className="px-3 py-2 w-44"></th>
              </tr>
            </thead>
            <tbody>
              {loans.map((l) => {
                const isPaidOff = (l.balance || 0) <= 0;
                return (
                  <tr key={l.id} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-medium">
                      {l.lender}
                      {l.partner_name && (
                        <div className="text-[10px] text-slate-400">By {l.partner_name}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">{formatCurrency(l.principal)}</td>
                    <td className="px-3 py-2 text-right">{formatCurrency(l.emi)}</td>
                    <td className="px-3 py-2 text-right">
                      <span className="font-medium">{l.paid_emis || 0}</span>
                      <span className="text-slate-400">/{l.tenure_months || 0}</span>
                    </td>
                    <td className="px-3 py-2 text-right text-emerald-600">{formatCurrency(l.paid_total)}</td>
                    <td className={`px-3 py-2 text-right font-medium ${isPaidOff ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {formatCurrency(l.balance)}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {isPaidOff ? (
                        <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Paid off</span>
                      ) : (
                        l.next_due_date || '—'
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-purple-600 disabled:opacity-30"
                        onClick={() => payEMI(l)}
                        disabled={isPaidOff}
                        data-testid={`pay-emi-${l.id}`}
                        title="Record this month's EMI"
                      >
                        <IndianRupee className="h-3.5 w-3.5 mr-1" /> Pay EMI
                      </Button>
                      <Button size="sm" variant="ghost" className="text-slate-600" onClick={() => openPayments(l)} data-testid={`loan-history-${l.id}`}>
                        History
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => remove(l)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {/* Add Loan */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add Loan</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Lender *</Label>
              <Input value={form.lender} onChange={(e) => setForm({ ...form, lender: e.target.value })} required data-testid="loan-lender-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Loan Amount (₹) *</Label>
                <Input
                  type="number"
                  value={form.principal}
                  onChange={(e) => setForm({ ...form, principal: e.target.value })}
                  required
                  data-testid="loan-principal-input"
                />
              </div>
              <div>
                <Label>EMI Amount (₹) *</Label>
                <Input
                  type="number"
                  value={form.emi}
                  onChange={(e) => setForm({ ...form, emi: e.target.value })}
                  required
                  data-testid="loan-emi-input"
                />
              </div>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-md p-2 text-xs text-purple-900" data-testid="loan-tenure-preview">
              Number of EMIs: <span className="font-semibold">{previewTenure() || '—'}</span>
              {previewTenure() > 0 && form.start_date && ` · First EMI on ${form.start_date}`}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Default EMI Payer</Label>
                <Select
                  value={form.partner_id || 'none'}
                  onValueChange={(v) => setForm({ ...form, partner_id: v === 'none' ? '' : v })}
                >
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {partners.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="submit-loan-btn">Add Loan</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Loan Payment History */}
      <Dialog open={!!payOpen} onOpenChange={(o) => !o && setPayOpen(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>EMI History — {payOpen?.lender}</DialogTitle>
          </DialogHeader>
          {payOpen && (
            <div className="text-sm text-slate-600 mb-2">
              Loan: {formatCurrency(payOpen.principal)} ·
              EMI: {formatCurrency(payOpen.emi)} ×{' '}
              <span className="font-medium">{payOpen.paid_emis || 0}/{payOpen.tenure_months || 0}</span> ·
              Balance: <span className="text-rose-600 font-medium">{formatCurrency(payOpen.balance)}</span>
            </div>
          )}
          <div className="mt-2 max-h-96 overflow-y-auto">
            {payments.length === 0 ? (
              <p className="text-center text-slate-500 py-6 text-sm">No EMIs paid yet. Click "Pay EMI" to record one, or wait for the daily cron to auto-post overdue EMIs.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                    <th className="px-3 py-2 text-left">Source</th>
                    <th className="px-3 py-2 text-left">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.id} className="border-t border-slate-100">
                      <td className="px-3 py-2">{p.date}</td>
                      <td className="px-3 py-2 text-right font-medium">{formatCurrency(p.amount)}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          p.source === 'auto_cron' ? 'bg-purple-100 text-purple-700' :
                          p.source === 'manual' ? 'bg-blue-100 text-blue-700' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {p.source || 'legacy'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-500">{p.notes || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ========================= LEDGER ========================= */
const LedgerPanel = ({ ledger }) => {
  const { rows = [], totals = { inflow: 0, outflow: 0, net: 0 } } = ledger;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-3">
        <Card className="p-4 border-emerald-200 bg-emerald-50">
          <p className="text-xs text-emerald-700 uppercase">Inflow</p>
          <p className="text-2xl font-heading text-emerald-700">{formatCurrency(totals.inflow)}</p>
        </Card>
        <Card className="p-4 border-rose-200 bg-rose-50">
          <p className="text-xs text-rose-700 uppercase">Outflow</p>
          <p className="text-2xl font-heading text-rose-700">{formatCurrency(totals.outflow)}</p>
        </Card>
        <Card className={`p-4 ${totals.net >= 0 ? 'border-blue-200 bg-blue-50' : 'border-amber-200 bg-amber-50'}`}>
          <p className={`text-xs uppercase ${totals.net >= 0 ? 'text-blue-700' : 'text-amber-700'}`}>Net</p>
          <p className={`text-2xl font-heading ${totals.net >= 0 ? 'text-blue-700' : 'text-amber-700'}`}>
            {formatCurrency(totals.net)}
          </p>
        </Card>
      </div>

      {rows.length === 0 ? (
        <Card className="p-8 text-center border-dashed text-slate-500">
          No ledger transactions yet. Add expenses, rents, partner payments or loan EMIs to populate.
        </Card>
      ) : (
        <Card className="border-slate-200 overflow-hidden">
          <div className="max-h-[480px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left">Date</th>
                  <th className="px-4 py-2 text-left">Type</th>
                  <th className="px-4 py-2 text-left">Label</th>
                  <th className="px-4 py-2 text-left">By</th>
                  <th className="px-4 py-2 text-left">Notes</th>
                  <th className="px-4 py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const positive = r.amount > 0;
                  return (
                    <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-2 whitespace-nowrap">{r.date}</td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          r.kind === 'expense' ? 'bg-rose-100 text-rose-700' :
                          r.kind === 'rent' ? 'bg-emerald-100 text-emerald-700' :
                          r.kind === 'partner_investment' ? 'bg-blue-100 text-blue-700' :
                          r.kind === 'partner_drawing' ? 'bg-amber-100 text-amber-700' :
                          'bg-purple-100 text-purple-700'
                        }`}>
                          {r.kind.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-2">{r.label}</td>
                      <td className="px-4 py-2 text-slate-600">{r.by || '—'}</td>
                      <td className="px-4 py-2 text-slate-500 truncate max-w-[200px]">{r.notes || '—'}</td>
                      <td className={`px-4 py-2 text-right font-medium ${positive ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {positive ? '+' : '−'}{formatCurrency(Math.abs(r.amount))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default PropertyDetail;
